interface SortByPrefixGroupedDescArgs<T> {
  items: T[];
  getLabel: (item: T) => string;
  getSize: (item: T) => number;
}

type SortByPrefixGroupedDesc = <T>(args: SortByPrefixGroupedDescArgs<T>) => T[];

const getPrefixKey = (label: string): string => {
  const trimmedLabel = label.trim();
  const firstToken = trimmedLabel.split(/\s+/)[0];
  return firstToken || trimmedLabel;
};

export const sortByPrefixGroupedDesc: SortByPrefixGroupedDesc = ({
  items,
  getLabel,
  getSize,
}) => {
  const itemsByPrefix = new Map<string, typeof items>();

  for (const item of items) {
    const prefix = getPrefixKey(getLabel(item));
    const bucket = itemsByPrefix.get(prefix);
    if (bucket) {
      bucket.push(item);
      continue;
    }
    itemsByPrefix.set(prefix, [item]);
  }

  for (const bucket of itemsByPrefix.values()) {
    bucket.sort((firstItem, secondItem) => {
      const sizeDifference = getSize(secondItem) - getSize(firstItem);
      if (sizeDifference !== 0) return sizeDifference;
      return getLabel(firstItem).localeCompare(getLabel(secondItem));
    });
  }

  const maxSizeByPrefix = new Map<string, number>();
  for (const [prefix, bucket] of itemsByPrefix) {
    let maxSize = -Infinity;
    for (const item of bucket) {
      const size = getSize(item);
      if (size > maxSize) maxSize = size;
    }
    maxSizeByPrefix.set(prefix, maxSize);
  }

  const sortedPrefixes = [...itemsByPrefix.keys()].sort(
    (firstPrefix, secondPrefix) => {
      const sizeDifference =
        (maxSizeByPrefix.get(secondPrefix) ?? 0) -
        (maxSizeByPrefix.get(firstPrefix) ?? 0);
      if (sizeDifference !== 0) return sizeDifference;
      return firstPrefix.localeCompare(secondPrefix);
    },
  );

  const sortedItems: typeof items = [];
  for (const prefix of sortedPrefixes) {
    const bucket = itemsByPrefix.get(prefix);
    if (!bucket) continue;
    sortedItems.push(...bucket);
  }

  return sortedItems;
};
